import { UzBadge, UzDescriptionList, UzDrawer, UzErrorState, UzSpinner } from '@utazzo/ui';
import React from 'react';

import { DELIVERY_OUTCOME_LABEL, DELIVERY_OUTCOME_TONE, formatDateTime } from '../labels';
import { lpsErrorText, useLpsDelivery } from '../use-lps';

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="space-y-2">
    <h3 className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">{title}</h3>
    {children}
  </section>
);

const Json = ({ value }: { value: unknown }) => (
  <pre className="max-h-96 overflow-auto rounded-md bg-zinc-950 p-3 font-mono text-[11px] leading-relaxed text-zinc-100">
    {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
  </pre>
);

/** One webhook request exactly as LPS received it, and what LPS did with it. */
export const LpsDeliveryDrawer = ({
  deliveryId,
  onClose,
}: {
  deliveryId: string | null;
  onClose: () => void;
}): React.JSX.Element => {
  const delivery = useLpsDelivery(deliveryId);
  const d = delivery.data;

  return (
    <UzDrawer
      open={Boolean(deliveryId)}
      onOpenChange={(open) => !open && onClose()}
      title="Webhook delivery"
      description={
        d ? `${d.connectionName ?? d.connectionId} · ${formatDateTime(d.receivedAt)}` : undefined
      }
    >
      {delivery.isLoading && (
        <div className="flex justify-center py-10">
          <UzSpinner />
        </div>
      )}
      {delivery.isError && (
        <UzErrorState
          size="compact"
          title="Could not load this delivery"
          description={lpsErrorText(delivery.error, 'It may have passed the 90-day retention.')}
          onRetry={() => void delivery.refetch()}
        />
      )}
      {d && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <UzBadge tone={DELIVERY_OUTCOME_TONE[d.outcome]}>
              {DELIVERY_OUTCOME_LABEL[d.outcome]}
            </UzBadge>
            <span className="font-mono text-xs text-zinc-500">
              {d.method} · HTTP {d.httpStatus}
              {d.durationMs !== null ? ` · ${d.durationMs} ms` : ''}
            </span>
          </div>

          <Section title="Result">
            <UzDescriptionList
              layout="inline"
              density="compact"
              items={[
                { term: 'Connector', description: d.connectorId ?? '-' },
                { term: 'New leads', description: d.accepted },
                { term: 'Duplicates', description: d.duplicates },
                { term: 'Skipped', description: d.skipped },
                {
                  term: 'Lead ids',
                  description: d.leadIds.length ? (
                    <span className="font-mono text-xs break-all">{d.leadIds.join(', ')}</span>
                  ) : (
                    '-'
                  ),
                },
                { term: 'Size', description: `${d.bodyBytes.toLocaleString('en-IN')} bytes` },
              ]}
            />
          </Section>

          {(d.notes.length > 0 || d.error) && (
            <Section title="Why">
              <ul className="list-disc space-y-1 pl-5 text-sm text-zinc-700">
                {d.notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
              {d.error && <Json value={d.error} />}
            </Section>
          )}

          <Section title="Raw body">
            {d.body !== null && d.body !== undefined ? (
              <Json value={d.body} />
            ) : d.bodyText ? (
              <Json value={d.bodyText} />
            ) : (
              <p className="text-sm text-zinc-500">Empty.</p>
            )}
          </Section>

          {d.query && Object.keys(d.query).length > 0 && (
            <Section title="Query string (secrets hidden)">
              <Json value={d.query} />
            </Section>
          )}
          {d.headers && (
            <Section title="Request">
              <Json value={d.headers} />
            </Section>
          )}
        </div>
      )}
    </UzDrawer>
  );
};
